import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import searchIcon from "../../assets/icons/Search.png";
import filterIcon from "../../assets/icons/filter2.png";
import crateIcon from "../../assets/icons/crate.png";
import avatar from "../../assets/icons/user-avatar2.png";
import { logout, signoutUser } from "../../store";

import AvatarDropdown from "./AvatarDropdown";
import CrateSidebar from "./CrateSidebar";
import FilterModal from "../Common/AdvanceFilterModal";
import SearchBar from "./SearchBar";

import SupremeLogo from "../Common/SupremeLogo";

const Navbar = ({ toggleSidebar }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCrateOpen, setIsCrateOpen] = useState(false);
  const crateCount = 0;
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  const crateItems = useSelector((state) => state.crate.items);

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [shouldRenderFilter, setShouldRenderFilter] = useState(false);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const avatarRef = useRef(null);
  const dropdownRef = useRef(null);
  const crateRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // We'll only update the session storage when the search is actually executed
  // not on every change of the search input
  // This prevents premature storage of temporary input values

  // Update search keyword from filters but only on initial load or page navigation
  useEffect(() => {
    // This flag ensures we only load from storage on mount or navigation
    const shouldLoadFromStorage = !isSearchFocused;

    if (!shouldLoadFromStorage) {
      return;
    }

    let keyword = "";

    // First check if we have location state with filters (most up-to-date)
    if (location.state?.filters) {
      keyword = location.state.filters.keyword || "";
    } else {
      // If location.state is null or doesn't have filters, try session storage
      const savedFilters = sessionStorage.getItem("filters");
      if (savedFilters) {
        try {
          const parsedFilters = JSON.parse(savedFilters);
          keyword = parsedFilters.keyword || "";
        } catch (e) {
          console.error("Error parsing filters from session storage", e);
        }
      }
    }

    // Only update if we have a keyword to set
    if (keyword !== searchKeyword) {
      setSearchKeyword(keyword);
    }
    // Only depend on location changes, not search state
  }, [location.state, location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        avatarRef.current &&
        !avatarRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }

      if (
        crateRef.current &&
        !crateRef.current.contains(e.target) &&
        !e.target.closest("#crate-icon")
      ) {
        setIsCrateOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 0);

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const toggleFilterModal = () => {
    if (isFilterVisible) {
      setIsFilterVisible(false);
      setTimeout(() => setShouldRenderFilter(false), 300);
    } else {
      // Before showing the filter modal, update session storage with current search keyword
      // This ensures the filter modal has the latest search keyword value
      try {
        // Default structure for filters if none exists
        const defaultFilters = {
          genre: "All Genres",
          version: "All Versions",
          decade: "All Decades",
          key: [],
          bpm: [50, 160],
          popularity: [0, 100],
          danceability: [0, 100],
          energy: [0, 100],
          valence: [0, 100],
          tags: [],
          keyword: "",
        };

        // Get existing filters or use default
        let savedFilters;
        try {
          savedFilters = JSON.parse(sessionStorage.getItem("filters") || "{}");
        } catch (e) {
          console.error("Error parsing saved filters:", e);
          savedFilters = {};
        }

        // Make sure we have valid arrays
        const validatedFilters = {
          ...defaultFilters,
          ...savedFilters,
          // Force these to be arrays even if corrupted
          key: Array.isArray(savedFilters.key) ? savedFilters.key : [],
          tags: Array.isArray(savedFilters.tags) ? savedFilters.tags : [],
          keyword: searchKeyword, // Always use current search keyword
        };

        sessionStorage.setItem("filters", JSON.stringify(validatedFilters));
      } catch (error) {
        console.error("Error updating filters in session storage:", error);
      }

      setShouldRenderFilter(true);
      setTimeout(() => setIsFilterVisible(true), 10);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    // Even if the keyword is empty, we'll still process it to enable clearing the search
    const trimmedKeyword = searchKeyword.trim();

    // Get existing filters or use default
    const savedFilters = sessionStorage.getItem("filters");
    const defaultFilters = {
      genre: "All Genres",
      version: "All Versions",
      decade: "All Decades",
      key: [],
      bpm: [50, 160],
      popularity: [0, 100],
      danceability: [0, 100],
      energy: [0, 100],
      valence: [0, 100],
      tags: [],
      keyword: "",
    };

    let filters;
    try {
      filters = savedFilters ? JSON.parse(savedFilters) : { ...defaultFilters };
    } catch (e) {
      console.error("Error parsing saved filters:", e);
      filters = { ...defaultFilters };
    }

    // Update the keyword in filters - always set it (even if empty)
    filters.keyword = trimmedKeyword;

    // Save to session storage
    sessionStorage.setItem("filters", JSON.stringify(filters));

    // Navigate to search page with updated filters and current location as redirect
    // Always navigate to ensure the page updates properly
    navigate("/search", {
      state: { filters, redirect: location.pathname },
    });

    // Ensure the search input reflects the current keyword
    setSearchKeyword(trimmedKeyword);
  };

  // Create a single ref for the search input
  const searchInputRef = useRef(null);

  // Handle clicks on the search icon
  const handleSearchIconClick = (e) => {
    e.stopPropagation(); // Stop event propagation
    handleSearch({ preventDefault: () => {} });
  };

  /* 	// Initialize filters on mount
	useEffect(() => {
		// Check if we need to initialize the default filters
		if (!sessionStorage.getItem("filters")) {
			const defaultFilters = {
				genre: "All Genres",
				version: "All Versions",
				decade: "All Decades",
				key: [],
				bpm: [50, 160],
				popularity: [0, 100],
				danceability: [0, 100],
				energy: [0, 100],
				valence: [0, 100],
				tags: [],
				keyword: "",
			};
			sessionStorage.setItem("filters", JSON.stringify(defaultFilters));
		}
	}, []); */

  // Clear the search keyword only when navigating away from the search page
  useEffect(() => {
    const isNavigatingAway = location.pathname !== "/search";
    if (isNavigatingAway) {
      setSearchKeyword("");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleOpenFilter = () => toggleFilterModal();
    window.addEventListener("openFilterModal", handleOpenFilter);
    return () =>
      window.removeEventListener("openFilterModal", handleOpenFilter);
  }, []);

  return (
    <>
      {!shouldRenderFilter && (
        <header
          className={`relative mt-[-50px] sm:mt-[-60px] lg:mt-[-78px] top-0 z-[5000] md:z-50 h-[40px] sm:h-[60px] lg:h-[80px] w-full transition-transform duration-300 text-white left-0
    
          ${
            showNavbar
              ? scrolled
                ? "bg-[#080B12]/10 backdrop-blur-md"
                : "bg-transparent"
              : "-translate-y-full"
          }`}
        >
          {/* Mobile and Desktop View */}
          <div className=" min-[1800px]:max-w-[1648px] mx-auto px-4 sm:px-6 lg:px-8 h-full">
            <div className="flex items-center justify-between h-full px-1 sm:px-2 lg:px-0">
              <Link
                to="/home"
                className="block lg:hidden cursor-pointer hover:opacity-80 transition-opacity z-[6000] md:z-50"
              >
                <div translate="no" className="flex items-center w-[100px] h-[30px] sm:w-[120px] sm:h-[32px] md:w-[144.58px] md:h-[40.64px] notranslate">
                  <SupremeLogo />
                </div>
              </Link>

              {/* Desktop Search Bar */}
              <div className="hidden lg:flex items-center gap-2 w-[50%] max-w-[595px] h-[42px]">
                <SearchBar
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  onSearch={handleSearch}
                  onToggleFilter={toggleFilterModal}
                  isFocused={isSearchFocused}
                  setIsFocused={setIsSearchFocused}
                  isMobile={true} // or false for desktop
                />
              </div>

              {/* Right Side Actions */}
              <div className="flex items-center ml-1 sm:ml-2 gap-3 sm:gap-5 lg:gap-7">
                {/* Crate Icon */}
                <div
                  id="crate-icon"
                  className="relative cursor-pointer hover:bg-[#1E1E1E] transition-all duration-300 p-1 sm:p-0"
                  onClick={() => setIsCrateOpen(true)}
                >
                  {/* SVG Crate Icon */}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-[20px] h-[20px] sm:w-[22px] sm:h-[22px] lg:w-[24px] lg:h-[24px]"
                  >
                    <path
                      d="M21.6698 14.3L21.2698 19.3C21.1198 20.83 20.9998 22 18.2898 22H5.70977C2.99977 22 2.87977 20.83 2.72977 19.3L2.32977 14.3C2.24977 13.47 2.50977 12.7 2.97977 12.11L2.99977 12.09C3.54977 11.42 4.37977 11 5.30977 11H18.6898C19.6198 11 20.4398 11.42 20.9798 12.07C20.9898 12.08 20.9998 12.09 20.9998 12.1C21.4898 12.69 21.7598 13.46 21.6698 14.3Z"
                      stroke="white"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M3.5 11.4298V6.27978C3.5 2.87978 4.35 2.02979 7.75 2.02979H9.02C10.29 2.02979 10.58 2.40979 11.06 3.04978L12.33 4.74978C12.65 5.16978 12.84 5.42978 13.69 5.42978H16.24C19.64 5.42978 20.49 6.27978 20.49 9.67978V11.4698"
                      stroke="white"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.43 16.9998H14.57"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="19"
                      cy="5"
                      r="3.5"
                      fill="#FF0000"
                      stroke="#0A0A0A"
                    />
                  </svg>
                  <span className="absolute -top-0.5 sm:-top-1 -right-0.5 sm:-right-1 bg-[#FF000D] text-white text-[8px] sm:text-[10px] px-[3px] sm:px-[5px] rounded-full leading-none border border-black min-w-[14px] sm:min-w-[16px] h-[14px] sm:h-[16px] flex items-center justify-center">
                    {crateItems?.length}
                  </span>
                </div>

                {/* Avatar */}
                <div className="relative" ref={avatarRef}>
                  <div
                    className="w-[32px] h-[32px] sm:w-[38px] sm:h-[38px] lg:w-[44px] lg:h-[44px] rounded-full overflow-hidden flex items-center justify-center hover:bg-[#1E1E1E] transition-all duration-300"
                    onClick={() => setShowDropdown((prev) => !prev)}
                  >
                    <img
                      src={avatar}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover cursor-pointer"
                    />
                  </div>
                  {showDropdown && (
                    <div ref={dropdownRef}>
                      <AvatarDropdown
                        onLogout={async () => {
                          await dispatch(signoutUser());
                          dispatch(logout());
                        }}
                        onClose={() => setShowDropdown(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      <CrateSidebar
        ref={crateRef}
        isOpen={isCrateOpen}
        onClose={() => setIsCrateOpen(false)}
      />

      {shouldRenderFilter && (
        <FilterModal
          isVisible={isFilterVisible}
          onClose={toggleFilterModal}
          searchKeyword={searchKeyword}
          onKeywordChange={(newKeyword) => setSearchKeyword(newKeyword)}
        />
      )}
    </>
  );
};

export default Navbar;
